/**
 * Simple authentication state management
 * In a real application, this would use proper session management or JWT tokens
 */

const AUTH_KEY = 'petclinic_auth';

export interface AuthState {
  username: string;
  password?: string; // Store password for Basic Auth (in sessionStorage for security)
  enabled: boolean;
  roles: Array<{ name: string }>;
}

export const auth = {
  /**
   * Save authentication state
   * Password is stored in sessionStorage for Basic Auth (cleared on browser close)
   */
  setAuth: (authState: AuthState) => {
    if (typeof window !== 'undefined') {
      // Store username and roles in localStorage
      const { password, ...authDataWithoutPassword } = authState;
      localStorage.setItem(AUTH_KEY, JSON.stringify(authDataWithoutPassword));
      // Store password in sessionStorage (more secure, cleared on tab close)
      if (password) {
        sessionStorage.setItem(AUTH_KEY + '_password', password);
      }
    }
  },

  /**
   * Get current authentication state
   */
  getAuth: (): AuthState | null => {
    if (typeof window !== 'undefined') {
      const authData = localStorage.getItem(AUTH_KEY);
      return authData ? JSON.parse(authData) : null;
    }
    return null;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    return auth.getAuth() !== null;
  },

  /**
   * Clear authentication state (logout)
   */
  clearAuth: () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(AUTH_KEY);
      sessionStorage.removeItem(AUTH_KEY + '_password');
    }
  },

  /**
   * Get current username
   */
  getUsername: (): string | null => {
    const authState = auth.getAuth();
    return authState ? authState.username : null;
  },

  /**
   * Get password for Basic Auth (from sessionStorage)
   */
  getPassword: (): string | null => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(AUTH_KEY + '_password');
    }
    return null;
  },

  /**
   * Get Basic Auth header value
   */
  getBasicAuthHeader: (): string | null => {
    const username = auth.getUsername();
    const password = auth.getPassword();
    if (username && password) {
      const credentials = btoa(`${username}:${password}`);
      return `Basic ${credentials}`;
    }
    return null;
  }
};
